import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useSettings } from '@/contexts/SettingsContext';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, ArrowRight, AlertCircle } from 'lucide-react';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [logoError, setLogoError] = useState(false);
  const { login, user } = useAuth();
  const { getPrimaryColor, getLogo, getAuthPageLogo, getTitle } = useSettings();
  const navigate = useNavigate();

  // Helper function to darken a color
  const darkenColor = (color: string, percent: number): string => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const newR = Math.max(0, Math.floor(r * (1 - percent / 100)));
    const newG = Math.max(0, Math.floor(g * (1 - percent / 100)));
    const newB = Math.max(0, Math.floor(b * (1 - percent / 100)));
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };

  // Get primary color and calculate darker shades for gradient
  const primaryColor = getPrimaryColor();
  const logoUrl = getLogo();
  const authPageLogoUrl = getAuthPageLogo();
  const portalTitle = getTitle();
  const gradientColors = useMemo(() => {
    const darker = darkenColor(primaryColor, 25);
    const darkest = darkenColor(primaryColor, 45);
    return {
      start: primaryColor, // Use primary color as start
      end: darkest, // Darkest shade as end
      mid: darker, // Mid shade for decorative elements
    };
  }, [primaryColor]);

  // Redirect to dashboard if user is already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await login(email, password);
    } catch (error: any) {
      console.error('Login failed:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Invalid email or password. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-vh-100 position-relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${gradientColors.start} 0%, ${gradientColors.end} 100%)` }}>
      {/* Background Elements */}
      <div className="position-absolute top-0 start-0 w-100 h-100">
        <div className="position-absolute" style={{ top: '10%', left: '5%', width: '200px', height: '200px', background: `linear-gradient(135deg, ${gradientColors.start} 0%, ${gradientColors.end} 100%)`, borderRadius: '20px', transform: 'rotate(15deg)', opacity: 0.9 }}></div>
        <div className="position-absolute" style={{ top: '20%', right: '10%', width: '150px', height: '150px', background: `linear-gradient(135deg, ${gradientColors.end} 0%, ${gradientColors.mid} 100%)`, borderRadius: '50%', opacity: 0.9 }}></div>
        <div className="position-absolute" style={{ bottom: '20%', left: '15%', width: '100px', height: '100px', background: `linear-gradient(135deg, ${gradientColors.start} 0%, ${gradientColors.end} 100%)`, borderRadius: '15px', transform: 'rotate(-30deg)', opacity: 0.9 }}></div>
        <div className="position-absolute" style={{ bottom: '30%', right: '20%', width: '120px', height: '120px', background: `linear-gradient(135deg, ${gradientColors.mid} 0%, ${gradientColors.end} 100%)`, borderRadius: '25px', transform: 'rotate(45deg)', opacity: 0.9 }}></div>
      </div>

      <div className="container position-relative" style={{ zIndex: 10 }}>
        <div className="row justify-content-center min-vh-100 align-items-center">
          <div className="col-lg-10 col-xl-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="position-relative"
            >
              {/* Main Content Container */}
              <div className="row g-0 shadow-lg rounded-4 overflow-hidden bg-white" style={{ minHeight: '600px' }}>
                {/* Left Side - Branding */}
                <div className="col-lg-6 position-relative d-none d-lg-flex" style={{ background: 'linear-gradient(135deg, var(--primary, #1e40af) 0%, var(--primary-dark, #1e3a8a) 100%)' }}>
                  <div className="position-relative p-3 p-md-4 p-lg-5 h-100 d-flex flex-column justify-content-center text-white" style={{ overflow: 'auto' }}>
                    <div className="text-center mb-4 mb-md-5">
                      {/* Logo */}
                      <div className="d-flex align-items-center justify-content-center mx-auto mb-3 mb-md-4" style={{ maxWidth: '100%', padding: '0 1rem' }}>
                        {logoError ? (
                          <span className="fs-1 fw-bold text-white" style={{ fontSize: 'clamp(1.5rem, 4vw, 3rem)' }}>{portalTitle}</span>
                        ) : (
                          <img
                            src={authPageLogoUrl || logoUrl || '/scholar_vest_logo.png'}
                            alt={`${portalTitle} Agent Portal Logo`}
                            loading="eager"
                            style={{
                              height: "auto",
                              width: "auto",
                              maxHeight: "clamp(32px, 6.5vw, 64px)",
                              maxWidth: "100%",
                              objectFit: "contain",
                              objectPosition: "center"
                            }}
                            onError={() => setLogoError(true)}
                          />
                        )}
                      </div>
                      {/* <h1 className="display-4 fw-bold mb-2">{portalTitle}</h1> */}
                      <p className="fs-5 text-white text-opacity-90">Agent Portal</p>
                      <h6 className="fs-2 fw-bold mb-3">Welcome Back!</h6>
                    </div>
                    
                    <div className="text-center">
                      <p className="fs-5 text-white text-opacity-90 mb-0">
                        Sign in to manage your applications and track commissions
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Side - Form */}
                <div className="col-lg-6 p-3 p-md-4 p-lg-5 d-flex flex-column justify-content-center" style={{ overflow: 'auto' }}>
                  <div className="w-100">
                    {/* Logo for mobile */}
                    <div className="text-center mb-4 d-lg-none" style={{ padding: '0 0.5rem' }}>
                      <div className="d-inline-flex align-items-center justify-content-center mb-3" style={{ maxWidth: '100%' }}>
                        {logoError ? (
                          <span className="fs-2 fw-bold text-primary" style={{ fontSize: 'clamp(1.25rem, 5vw, 2rem)' }}>{portalTitle}</span>
                        ) : (
                          <img
                            src={logoUrl || authPageLogoUrl || '/scholar_vest_logo_full.png'}
                            alt={`${portalTitle} Agent Portal Logo`}
                            loading="eager"
                            style={{
                              height: "auto",
                              width: "auto",
                              maxHeight: "clamp(32px, 8vw, 56px)",
                              maxWidth: "100%",
                              objectFit: "contain",
                              objectPosition: "center"
                            }}
                            onError={() => setLogoError(true)}
                          />
                        )}
                      </div>
                      <h2 className="fs-3 fw-bold text-dark mb-1">Agent Portal</h2>
                    </div>

                    <div className="text-center mb-4">
                      <h3 className="fs-2 fw-bold text-dark mb-2">Sign In</h3>
                      <p className="text-muted">Enter your credentials to access your agent portal</p>
                    </div>

                    {error && (
                      <div className="alert alert-danger d-flex align-items-center mb-3" role="alert">
                        <AlertCircle size={18} className="me-2" />
                        <small>{error}</small>
                      </div>
                    )}

                    <form onSubmit={handleSubmit}>
                      {/* Email */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold text-dark">Email Address</label>
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0">
                            <Mail size={18} className="text-muted" />
                          </span>
                          <input
                            type="email"
                            className="form-control border-start-0"
                            name="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            required
                          />
                        </div>
                      </div>

                      {/* Password */}
                      <div className="mb-4">
                        <label className="form-label fw-semibold text-dark">Password</label>
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0">
                            <Lock size={18} className="text-muted" />
                          </span>
                          <input
                            type={showPassword ? 'text' : 'password'}
                            className="form-control border-start-0"
                            name="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter your password"
                            required
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary border-start-0"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>

                      {/* Remember Me & Forgot Password */}
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="rememberMe"
                          />
                          <label className="form-check-label small text-muted" htmlFor="rememberMe">
                            Remember me
                          </label>
                        </div>
                        <Link to="/forgot-password" className="small text-primary text-decoration-none">
                          Forgot password?
                        </Link>
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        className="btn btn-primary btn-lg w-100 mb-3 d-flex align-items-center justify-content-center gap-2"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <div className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            Signing in...
                          </>
                        ) : (
                          <>
                            Sign In
                            <ArrowRight size={18} />
                          </>
                        )}
                      </button>

                      {/* Sign Up Link */}
                      <div className="text-center">
                        <p className="text-muted mb-0">
                          New agent account?{' '}
                          <Link to="/register" className="text-primary text-decoration-none fw-semibold">
                            Register here
                          </Link>
                        </p>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};
